/**
 * YouTube Annotation
 *
 * Renders YouTube caption tool results as a native chat visualizer.
 *
 * Primary responsibilities:
 * - Normalize caption tool output into a playable YouTube card
 * - Keep YouTube links inside the chat instead of thumbnail-only anchors
 *
 * Dependencies:
 * - YouTube visualizer
 * - Annotation types
 */

import { forwardRef } from 'react'

import { Annotation, Annotation2 } from '@/types/annotation'
import { YouTubeVisualizer } from '@/components/messages/youtube-visualizer'
import { parseYoutubeVideoMarkerPayload } from '@/lib/youtube-markers'

type YouTubeProps = {
  annotation: Annotation | Annotation2
}

const YouTube = forwardRef<HTMLDivElement, YouTubeProps>(
  ({ annotation }: { annotation: Annotation | Annotation2 }, ref) => {
    let result = annotation.webScraper__youtubeCaptions

    if (!result) {
      return null
    }

    if ('result' in result) {
      result = result.result
    }

    const firstCaption = result.subtitles?.[0]?.text
    const video = parseYoutubeVideoMarkerPayload(
      JSON.stringify({
        url: result.videoUrl,
        title: 'YouTube video',
        thumbnailUrl: result.imageUrl,
        summary: typeof firstCaption === 'string' ? firstCaption : undefined
      })
    )

    if (!video) return null

    return (
      <div ref={ref}>
        <YouTubeVisualizer videos={[video]} />
      </div>
    )
  }
)

YouTube.displayName = 'YouTube'

export { YouTube }
